import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  RefreshControl,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';
import { useAssignmentStore } from '../../stores/assignmentStore';
import { useAuthStore } from '../../stores/authStore';
import { useMaintenanceStore } from '../../stores/maintenanceStore';
import { KPICard } from '../../components/dashboard/KPICard';
import { WorkshopBreakdown } from '../../components/dashboard/WorkshopBreakdown';
import { AssignmentGraph } from '../../components/dashboard/AssignmentGraph';
import { ActiveAssignmentsTable } from '../../components/dashboard/ActiveAssignmentsTable';
import { TodayVsYesterday } from '../../components/dashboard/TodayVsYesterday';
import { AssignmentStatusChart } from '../../components/dashboard/AssignmentStatusChart';
import theme from '../../theme';

export const DashboardScreen = ({ navigation }: any) => {
  const { user } = useAuthStore();
  const { vehicles, loading: vehiclesLoading, fetchVehicles } = useVehicleStore();
  const { drivers, loading: driversLoading, fetchDrivers } = useDriverStore();
  const { assignments, loading: assignmentsLoading, fetchAssignments } = useAssignmentStore();
  const { jobCards, loading: jobCardsLoading, fetchJobCards } = useMaintenanceStore();
  const [refreshing, setRefreshing] = useState(false);

  const loading = vehiclesLoading || driversLoading || assignmentsLoading || jobCardsLoading;

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchAssignments();
    fetchJobCards();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchVehicles(),
      fetchDrivers(),
      fetchAssignments(),
      fetchJobCards(),
    ]);
    setRefreshing(false);
  };

  // Calculate all metrics
  const dashboardMetrics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Get first day of current month
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);

    // Vehicle stats
    const totalVehicles = vehicles.length;
    const availableVehicles = vehicles.filter(v => v.carStatus === 'available').length;
    const workingVehicles = vehicles.filter(v => v.carStatus === 'work').length;
    const activeAssignments = assignments.filter(a => a.status === 'active');
    
    // Workshop breakdown from job cards - NEW 5 CATEGORIES
    // Use ONLY job_cards as single source of truth for workshop data
    const activeJobCards = jobCards.filter(jc => 
      jc.jobStatus === 'pending' || jc.jobStatus === 'in_progress'
    );
    
    const workshopBreakdown = {
      underMaintenance: activeJobCards.filter(jc => jc.underMaintenance).length,
      accidentRepair: activeJobCards.filter(jc => jc.accidentRepair).length,
      underMaintenanceAlQouz: activeJobCards.filter(jc => jc.underMaintenanceAlQouz).length,
      underMaintenanceAgency: activeJobCards.filter(jc => jc.underMaintenanceAgency).length,
      others: activeJobCards.filter(jc => jc.others).length,
      total: activeJobCards.length, // ✅ FIXED: Use job cards count instead of vehicle status
    };

    // Today's assignments
    const assignmentsToday = assignments.filter(a => {
      const assignDate = new Date(a.assignment_date);
      assignDate.setHours(0, 0, 0, 0);
      return assignDate.getTime() === today.getTime();
    }).length;

    // Yesterday's data for comparison
    const assignmentsYesterday = assignments.filter(a => {
      const assignDate = new Date(a.assignment_date);
      assignDate.setHours(0, 0, 0, 0);
      return assignDate.getTime() === yesterday.getTime();
    }).length;

    const returnsToday = assignments.filter(a => {
      if (!a.return_date) return false;
      const returnDate = new Date(a.return_date);
      returnDate.setHours(0, 0, 0, 0);
      return returnDate.getTime() === today.getTime();
    }).length;

    const returnsYesterday = assignments.filter(a => {
      if (!a.return_date) return false;
      const returnDate = new Date(a.return_date);
      returnDate.setHours(0, 0, 0, 0);
      return returnDate.getTime() === yesterday.getTime();
    }).length;

    const workshopToday = vehicles.filter(v => {
      if (v.carStatus !== 'in_workshop') return false;
      const createdDate = v.created_at ? new Date(v.created_at) : null;
      if (!createdDate) return false;
      createdDate.setHours(0, 0, 0, 0);
      return createdDate.getTime() === today.getTime();
    }).length;

    const workshopYesterday = vehicles.filter(v => {
      if (v.carStatus !== 'in_workshop') return false;
      const createdDate = v.created_at ? new Date(v.created_at) : null;
      if (!createdDate) return false;
      createdDate.setHours(0, 0, 0, 0);
      return createdDate.getTime() === yesterday.getTime();
    }).length;

    // Daily assignment data from 1st of month to today - TOTAL assigned vehicles per day
    const dailyAssignmentData = [];
    const daysInMonth = today.getDate(); // Number of days from 1st to today
    
    for (let dayOffset = 0; dayOffset < daysInMonth; dayOffset++) {
      const date = new Date(firstDayOfMonth);
      date.setDate(date.getDate() + dayOffset);
      date.setHours(0, 0, 0, 0);
      
      // Count total vehicles assigned on this date (active assignments on that day)
      const count = assignments.filter(a => {
        const assignDate = new Date(a.assignment_date);
        assignDate.setHours(0, 0, 0, 0);
        
        // Check if assignment was active on this date
        const isAssignedByThisDate = assignDate.getTime() <= date.getTime();
        
        // Check if not returned yet, or returned after this date
        const notReturnedYet = !a.return_date || 
          new Date(a.return_date).setHours(0, 0, 0, 0) > date.getTime();
        
        return isAssignedByThisDate && notReturnedYet;
      }).length;
      
      dailyAssignmentData.push({
        date: date.toISOString(),
        count,
      });
    }

    // Calculate monthly average
    const totalAssignmentsThisMonth = dailyAssignmentData.reduce((sum, d) => sum + d.count, 0);
    const monthlyAverage = daysInMonth > 0 ? totalAssignmentsThisMonth / daysInMonth : 0;

    // Completed assignments today
    const completedToday = assignments.filter(a => {
      if (a.status !== 'completed') return false;
      const updatedDate = a.updated_at ? new Date(a.updated_at) : null;
      if (!updatedDate) return false;
      updatedDate.setHours(0, 0, 0, 0);
      return updatedDate.getTime() === today.getTime();
    }).length;

    return {
      totalVehicles,
      availableVehicles,
      workingVehicles,
      activeAssignments: activeAssignments.length,
      assignmentsToday,
      workshopBreakdown,
      dailyAssignmentData,
      monthlyAverage,
      activeAssignmentsList: activeAssignments,
      comparisonData: {
        assignmentsCreated: { today: assignmentsToday, yesterday: assignmentsYesterday },
        vehiclesReturned: { today: returnsToday, yesterday: returnsYesterday },
        workshopEntries: { today: workshopToday, yesterday: workshopYesterday },
      },
      statusData: {
        active: activeAssignments.length,
        completed: completedToday,
        total: assignments.length,
      },
    };
  }, [vehicles, assignments, jobCards]);

  const isWeb = Platform.OS === 'web';

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.welcomeText}>Welcome back</Text>
          <Text style={styles.userNameText}>
            {user?.email?.split('@')[0] || 'Admin'}
          </Text>
        </View>
        <TouchableOpacity 
          style={styles.profileButton} 
          onPress={() => navigation.navigate('Settings')}
        >
          <Ionicons name="settings-outline" size={28} color={theme.colors.primary} />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[theme.colors.primary]} />
        }
      >
        {/* Top KPI Cards */}
        <View style={styles.section}>
          <View style={[styles.kpiGrid, isWeb && styles.kpiGridWeb]}>
            <KPICard
              title="Total Fleet"
              value={dashboardMetrics.totalVehicles}
              icon="car"
              color={theme.colors.primary}
              subtitle={`${dashboardMetrics.totalVehicles} vehicles in fleet`}
              onPress={() => navigation.navigate('Vehicles')}
            />
            <KPICard
              title="Active Now"
              value={dashboardMetrics.workingVehicles}
              icon="car-sport"
              color={theme.colors.green600}
              subtitle={`Vehicles with status 'WORK'`}
              onPress={() => navigation.navigate('Vehicles')}
            />
            <KPICard
              title="Assigned Today"
              value={dashboardMetrics.assignmentsToday}
              icon="calendar"
              color={theme.colors.blue600}
              subtitle="New assignments created"
              trend={{
                value: dashboardMetrics.comparisonData.assignmentsCreated.yesterday > 0
                  ? Math.abs(
                      ((dashboardMetrics.assignmentsToday - dashboardMetrics.comparisonData.assignmentsCreated.yesterday) /
                        dashboardMetrics.comparisonData.assignmentsCreated.yesterday) *
                        100
                    )
                  : 0,
                direction: dashboardMetrics.assignmentsToday >= dashboardMetrics.comparisonData.assignmentsCreated.yesterday ? 'up' : 'down',
              }}
            />
            <KPICard
              title="In Workshop"
              value={dashboardMetrics.workshopBreakdown.total}
              icon="construct"
              color={theme.colors.orange600}
              subtitle="Vehicles under service"
              onPress={() => navigation.navigate('Maintenance')}
            />
          </View>
        </View>

        {/* Assignment Graph */}
        <View style={styles.section}>
          <AssignmentGraph
            data={dashboardMetrics.dailyAssignmentData}
            monthlyAverage={dashboardMetrics.monthlyAverage}
          />
        </View>

        {/* Two Column Section */}
        <View style={[styles.section, isWeb && styles.twoColumnSection]}>
          {/* Workshop Breakdown */}
          <View style={[styles.columnItem, isWeb && styles.columnItemWeb]}>
            <WorkshopBreakdown data={dashboardMetrics.workshopBreakdown} />
          </View>

          {/* Right Column */}
          <View style={[styles.columnItem, isWeb && styles.columnItemWeb]}>
            {/* Assignment Status Chart */}
            <AssignmentStatusChart
              data={dashboardMetrics.statusData}
              available={dashboardMetrics.availableVehicles}
            />

            {/* Today vs Yesterday */}
            <TodayVsYesterday data={dashboardMetrics.comparisonData} />
          </View>
        </View>

        {/* Active Assignments Table */}
        <View style={styles.section}>
          <ActiveAssignmentsTable
            assignments={dashboardMetrics.activeAssignmentsList}
            onViewDetails={(assignment) => {
              // Navigate to assignment details
              console.log('View assignment:', assignment.id);
            }}
            onReceiveVehicle={(assignment) => {
              // Navigate to receiving form
              navigation.navigate('Vehicles');
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.white,
    ...theme.shadows.sm,
  },
  welcomeText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  userNameText: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
  },
  profileButton: {
    padding: theme.spacing.xs,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    paddingHorizontal: theme.spacing.md,
    marginTop: theme.spacing.md,
  },
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  kpiGridWeb: {
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
  },
  twoColumnSection: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  columnItem: {
    flex: 1,
  },
  columnItemWeb: {
    minWidth: 0,
  },
});
